import React, { Component } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { DashboardAPI } from '../../../services/dashboardService';
import StatCard from './StatCard';
import ChartCard from './ChartCard';
import DonutChart from './DonutChart';
import ReactGA from 'react-ga4';
import './Dashboard.css';

const USE_MOCK_DATA = false;

class Dashboard extends Component {
  state = {
    loading: false,
    error: null,
    data: null,
    period: 30,
    isDesktop: window.innerWidth >= 1024
  };

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    this.loadData();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    this.setState({ isDesktop: window.innerWidth >= 1024 });
  };

  loadData = async () => {
    if (USE_MOCK_DATA) {
      this.setState({ data: this.getMockData(), loading: false });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const { typeId, userType, company } = this.props;
      const dealerId = userType === 'dealer' ? typeId : null;
      const installerId = userType === 'dealer' ? company?.installer_id : typeId;
      const locationId = this.props.selectedLocationId || null;
      const data = await DashboardAPI.getMetrics(installerId, this.state.period, dealerId, locationId);
      console.log('[Dashboard] API response RAW:', JSON.stringify(data, null, 2));
      this.setState({ data, loading: false });
    } catch (err) {
      console.error('[Dashboard] Error loading metrics:', err);
      this.setState({ error: 'Unable to load dashboard data. Please try again.', loading: false });
    }
  };

  handlePeriodChange = (days) => {
    this.setState({ period: days }, this.loadData);
    ReactGA.event({ category: 'Dashboard', action: 'Changed Period', label: `${days}d` });
  };

  // --- Installer (Specialist) View ---
  renderInstallerDashboard() {
    const { data } = this.state;
    if (!data) return null;

    const { aggregate, outstanding, payments, dailyTrend, leadAttribution, campaigns, customers } = data;
    const user = this.props.user || {};
    const isOwner = user.access === 'ADMIN' || user.role === 'admin' || user.role === 'owner';

    // Convert revenueBySource object to array for donut chart
    const sourceData = this.objectToArray(data.revenueBySource, 'source', { jobs: 'count', revenue: 'value' });

    // Convert serviceBreakdown for donut chart
    const serviceData = (data.serviceBreakdown || []).slice(0, 5).map(s => ({
      name: s.service_name,
      value: s.total_revenue
    }));

    // Convert payments.byMethod object to array
    const paymentData = this.objectToArray(payments?.byMethod, 'method', { count: 'count', amount: 'value' });

    // Build aging buckets from outstanding.jobs
    const agingBuckets = this.buildAgingBuckets(outstanding?.jobs || []);

    // Top lead source
    const topSource = leadAttribution?.bySource
      ? Object.entries(leadAttribution.bySource).sort((a, b) => b[1].revenue - a[1].revenue)[0]
      : null;

    // Lead source data for donut
    const leadSourceData = leadAttribution?.bySource
      ? Object.entries(leadAttribution.bySource).map(([name, d]) => ({ name, value: d.total }))
      : [];

    // Campaign data for donut
    const campaignData = (campaigns || []).map(c => ({ name: c.campaign_name, value: c.revenue }));

    return (
      <>
        {/* Top-Level KPIs */}
        <div className="overview-cards">
          <StatCard
            label="Leads"
            value={aggregate.leads.thisMonth}
            sublabel="this month"
            sparkData={this.buildSparkFromTrend(dailyTrend, 'jobs')}
          />
          <StatCard
            label="Booked Revenue"
            value={this.formatCurrency(aggregate.bookedRevenue)}
          />
          <StatCard
            label="Collected"
            value={this.formatCurrency(payments?.totalPaid || 0)}
            sublabel="this period"
            sparkData={this.buildSparkFromTrend(dailyTrend, 'revenue')}
          />
          <StatCard
            label="Outstanding"
            value={`${outstanding?.count || 0} / ${this.formatCurrency(outstanding?.totalRevenue || 0)}`}
            className={(outstanding?.count || 0) > 0 ? 'warning' : ''}
          />
          <StatCard
            label="Avg Ticket"
            value={this.formatCurrency(aggregate.totalJobs > 0 ? aggregate.bookedRevenue / aggregate.totalJobs : 0)}
          />
          {aggregate.totalMaterialCost != null && (
            <StatCard
              label="Material Cost"
              value={this.formatCurrency(aggregate.totalMaterialCost)}
              sublabel="this period"
            />
          )}
          {aggregate.totalMaterialCost != null && aggregate.bookedRevenue > 0 && (
            <StatCard
              label="Gross Margin"
              value={`${Math.round(((aggregate.bookedRevenue - aggregate.totalMaterialCost) / aggregate.bookedRevenue) * 100)}%`}
              sublabel={this.formatCurrency(aggregate.bookedRevenue - aggregate.totalMaterialCost)}
              className={((aggregate.bookedRevenue - aggregate.totalMaterialCost) / aggregate.bookedRevenue) >= 0.5 ? 'success' : ((aggregate.bookedRevenue - aggregate.totalMaterialCost) / aggregate.bookedRevenue) >= 0.3 ? '' : 'warning'}
            />
          )}
        </div>

        {/* Conversion metrics */}
        <div className="overview-cards" style={{ marginBottom: 24 }}>
          <StatCard
            label="Conversion Rate"
            value={`${Math.round(aggregate.leadToPaidRate)}%`}
            sublabel="lead to paid"
            className={aggregate.leadToPaidRate >= 50 ? 'success' : ''}
          />
          <StatCard
            label="Lead to Paid"
            value={`${aggregate.avgDaysLeadToPaid} days`}
            sublabel="average"
          />
          {customers && (
            <StatCard
              label="Repeat Rate"
              value={`${Math.round(customers.repeatRate)}%`}
              sublabel={`${customers.repeat} of ${customers.total} customers`}
            />
          )}
        </div>

        {/* Donut Charts Row */}
        <div className="chart-grid">
          <ChartCard title="Revenue by Source">
            <DonutChart
              data={sourceData.map(s => ({ name: s.source, value: s.value }))}
              formatValue={(v) => this.formatCurrency(v)}
              colors={['#2E56F6', '#FF9516', '#28a745']}
            />
          </ChartCard>
          <ChartCard title={`Revenue by Service (Top ${serviceData.length})`}>
            <DonutChart
              data={serviceData}
              formatValue={(v) => this.formatCurrency(v)}
            />
          </ChartCard>
        </div>

        {/* Revenue Trend */}
        {dailyTrend && dailyTrend.length > 0 && (
          <ChartCard title={`Revenue Trend (${this.state.period} days)`} className="trend-chart-card">
            <div style={{ width: '100%', height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyTrend}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#5196d3" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#5196d3" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                    tickFormatter={(d) => {
                      const dt = new Date(d);
                      return `${dt.getMonth() + 1}/${dt.getDate()}`;
                    }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                    tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
                    width={50}
                  />
                  <Tooltip
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']}
                    labelFormatter={(d) => new Date(d).toLocaleDateString()}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#5196d3"
                    fill="url(#revenueGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        )}

        {/* Pipeline Speed + Insights */}
        <div className="bottom-grid">
          <ChartCard title="Pipeline Speed">
            {(data.pipelineAge || []).length > 0 ? (
              <>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                  Average days between stages this period
                </div>
                {data.pipelineAge.map((stage, i) => {
                  const maxDays = Math.max(...data.pipelineAge.map(s => s.avgDays));
                  const widthPct = maxDays > 0 ? (stage.avgDays / maxDays) * 100 : 0;
                  const color = this.getPipelineStageColor(stage.stage, stage.avgDays);
                  return (
                    <div key={i} className="pipeline-stage">
                      <div className="pipeline-stage-label">{stage.stage}</div>
                      <div className="pipeline-stage-bar-wrap">
                        <div className="pipeline-stage-bar" style={{ width: `${Math.max(widthPct, 4)}%`, backgroundColor: color }} />
                      </div>
                      <div className="pipeline-stage-value">
                        {stage.avgDays % 1 === 0 ? stage.avgDays : stage.avgDays.toFixed(1)}d
                      </div>
                    </div>
                  );
                })}
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 10, textAlign: 'center' }}>
                  Total avg: {data.pipelineAge.reduce((s, st) => s + st.avgDays, 0).toFixed(1)} days lead to paid
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: 20 }}>
                Not enough data for this period
              </div>
            )}
          </ChartCard>

          {isOwner && (
            <ChartCard title="Insights">
              {(outstanding?.count || 0) > 0 && (
                <div className="insight-item">
                  <span className="insight-icon"><i className="fa-sharp fa-solid fa-triangle-exclamation" style={{ color: '#f59e0b' }} /></span>
                  <div>
                    <div>{outstanding.count} job{outstanding.count > 1 ? 's' : ''} awaiting payment</div>
                    <div className="insight-value">{this.formatCurrency(outstanding.totalRevenue)} outstanding</div>
                  </div>
                </div>
              )}
              {outstanding?.jobs?.some(j => j.days_since_finished >= 7) && (
                <div className="insight-item">
                  <span className="insight-icon"><i className="fa-sharp fa-solid fa-triangle-exclamation" style={{ color: '#dc3545' }} /></span>
                  <div>
                    <div>{outstanding.jobs.filter(j => j.days_since_finished >= 7).length} job{outstanding.jobs.filter(j => j.days_since_finished >= 7).length > 1 ? 's' : ''} unpaid &gt;7 days</div>
                    <div className="insight-value">
                      {this.formatCurrency(outstanding.jobs.filter(j => j.days_since_finished >= 7).reduce((s, j) => s + j.request_total, 0))} at risk
                    </div>
                  </div>
                </div>
              )}
              {topSource && (
                <div className="insight-item">
                  <span className="insight-icon"><i className="fa-sharp fa-solid fa-arrow-trend-up" style={{ color: '#28a745' }} /></span>
                  <div>
                    <div>Top Source: {topSource[0]}</div>
                    <div className="insight-value">{this.formatCurrency(topSource[1].revenue)} revenue</div>
                  </div>
                </div>
              )}
              {leadAttribution && (
                <div className="insight-item">
                  <span className="insight-icon"><i className="fa-sharp fa-solid fa-bolt" style={{ color: '#6f42c1' }} /></span>
                  <div>
                    <div>{Object.keys(leadAttribution.bySource || {}).length} lead sources active</div>
                    <div className="insight-value">{Math.round(leadAttribution.conversionRate)}% conversion rate</div>
                  </div>
                </div>
              )}
              {data.reviews?.rating && (
                <div className="insight-item">
                  <span className="insight-icon"><i className="fa-sharp fa-solid fa-star" style={{ color: '#f59e0b' }} /></span>
                  <div>
                    <div>{data.reviews.rating} stars ({data.reviews.totalReviews} reviews)</div>
                    <div className="insight-value">Google rating</div>
                  </div>
                </div>
              )}
            </ChartCard>
          )}
        </div>

        {/* Lead Sources + Awaiting Payment */}
        {(leadSourceData.length > 0 || agingBuckets.length > 0) && (
          <div className="chart-grid">
            {leadSourceData.length > 0 && (
              <ChartCard title="Lead Sources">
                <DonutChart
                  data={leadSourceData}
                  showPercent={true}
                  colors={['#2E56F6', '#28a745', '#6f42c1', '#FF9516', '#5196d3', '#f59e0b', '#dc3545']}
                />
              </ChartCard>
            )}
            {agingBuckets.length > 0 && (
              <ChartCard title="Awaiting Payment">
                <DonutChart
                  data={agingBuckets}
                  formatValue={(v) => this.formatCurrency(v)}
                  showPercent={false}
                />
              </ChartCard>
            )}
          </div>
        )}

        {/* Inventory Alerts + Top Materials */}
        {data.inventory && (
          <div className="chart-grid">
            <ChartCard title="Inventory Alerts">
              {(data.inventory.lowStockAlerts || []).length === 0 ? (
                <div className="insight-item">
                  <span className="insight-icon">
                    <i className="fa-sharp fa-solid fa-circle-check" style={{ color: '#28a745' }} />
                  </span>
                  <div>All products stocked</div>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
                    {data.inventory.lowStockAlerts.length} product{data.inventory.lowStockAlerts.length !== 1 ? 's' : ''} below reorder point
                  </div>
                  {data.inventory.lowStockAlerts.map(item => (
                    <div className="insight-item" key={item.product_id}>
                      <span className="insight-icon">
                        <i className="fa-sharp fa-solid fa-triangle-exclamation"
                           style={{ color: item.current_qty === 0 ? '#dc3545' : '#f59e0b' }} />
                      </span>
                      <div>
                        <div>{item.brand} {item.name}</div>
                        <div className="insight-value">
                          {item.current_qty} remaining (reorder at {item.reorder_point})
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </ChartCard>

            {(data.inventory.topMaterialsUsed || []).length > 0 && (
              <ChartCard title="Top Materials Used">
                <DonutChart
                  data={data.inventory.topMaterialsUsed.map(m => ({
                    name: m.category ? `${m.category} - ${m.name}` : m.name,
                    value: m.usage_count
                  }))}
                  showPercent={true}
                  colors={['#5196d3', '#FF9516', '#28a745', '#6f42c1', '#2E56F6']}
                />
              </ChartCard>
            )}
          </div>
        )}

        {/* Campaigns + Payment Methods */}
        {(campaignData.length > 0 || paymentData.length > 0) && (
          <div className="chart-grid">
            {campaignData.length > 0 && (
              <ChartCard title="Campaign Performance">
                <DonutChart
                  data={campaignData}
                  formatValue={(v) => this.formatCurrency(v)}
                  colors={['#6f42c1', '#5196d3', '#FF9516', '#28a745']}
                />
              </ChartCard>
            )}
            {paymentData.length > 0 && (
              <ChartCard title="Payment Methods">
                <DonutChart
                  data={paymentData.map(p => ({ name: p.method, value: p.value }))}
                  formatValue={(v) => this.formatCurrency(v)}
                  colors={['#2E56F6', '#FF9516', '#28a745', '#6f42c1']}
                />
              </ChartCard>
            )}
          </div>
        )}

        {/* Technician Performance */}
        {data.technicians && data.technicians.length > 0 && isOwner && (
          <ChartCard title="Technician Performance">
            <div className="tech-grid">
              <div className="tech-chart">
                <DonutChart
                  data={data.technicians.map(t => ({
                    name: t.name,
                    value: t.revenuePeriod
                  }))}
                  formatValue={(v) => this.formatCurrency(v)}
                  colors={['#5196d3', '#FF9516', '#28a745', '#6f42c1', '#2E56F6', '#f59e0b']}
                />
              </div>
              <div className="tech-table-wrap">
                <table className="tech-table">
                  <thead>
                    <tr>
                      <th>Technician</th>
                      <th>Completed</th>
                      <th>Avg Time</th>
                      <th>Revenue</th>
                      <th>Utilization</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.technicians.map(t => (
                      <tr key={t.id}>
                        <td style={{ fontWeight: 500 }}>{t.name}</td>
                        <td>{t.completedPeriod}</td>
                        <td>{t.avgServiceHours ? `${t.avgServiceHours}h` : '-'}</td>
                        <td>{this.formatCurrency(t.revenuePeriod)}</td>
                        <td>
                          <span style={{
                            color: t.utilizationPct >= 70 ? '#28a745' : t.utilizationPct >= 50 ? '#f59e0b' : '#dc3545',
                            fontWeight: 600
                          }}>
                            {t.utilizationPct}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </ChartCard>
        )}
      </>
    );
  }

  // --- Dealer View ---
  // Story: "The more you sell, the more margin you make"
  renderDealerDashboard() {
    const { data } = this.state;
    if (!data) return null;

    const { aggregate, outstanding, dailyTrend, serviceBreakdown } = data;

    // Compute dealer margin from serviceBreakdown (retail - wholesale)
    const totalRetail = (serviceBreakdown || []).reduce((s, svc) => s + (svc.total_retail || 0), 0);
    const totalWholesale = (serviceBreakdown || []).reduce((s, svc) => s + (svc.total_revenue || 0), 0);
    const totalMargin = totalRetail - totalWholesale;
    const requests = aggregate.totalJobs || 0;
    const avgMargin = requests > 0 ? Math.round(totalMargin / requests) : 0;
    const marginPct = totalRetail > 0 ? Math.round(totalMargin / totalRetail * 100) : 0;

    // Build margin by category from serviceBreakdown
    const categoryMap = {};
    (serviceBreakdown || []).forEach(svc => {
      const cat = svc.service_category || 'Other';
      if (!categoryMap[cat]) categoryMap[cat] = { category: cat, retail: 0, wholesale: 0, margin: 0, count: 0 };
      categoryMap[cat].retail += svc.total_retail || 0;
      categoryMap[cat].wholesale += svc.total_revenue || 0;
      categoryMap[cat].margin += (svc.total_retail || 0) - (svc.total_revenue || 0);
      categoryMap[cat].count += svc.service_count || 0;
    });
    const marginByCategory = Object.values(categoryMap).sort((a, b) => b.margin - a.margin);

    // Build margin trend from dailyTrend (approximate: apply overall margin % to daily revenue)
    const marginTrend = (dailyTrend || []).map(d => ({
      ...d,
      margin: marginPct > 0 ? Math.round(d.revenue * marginPct / 100) : 0
    }));

    // Pipeline from outstanding + aggregate
    const outstandingCount = outstanding?.count || 0;

    return (
      <>
        {/* Hero Metrics */}
        <div className="overview-cards">
          <StatCard
            label="Requests"
            value={requests}
            sublabel="this period"
            sparkData={this.buildSparkFromTrend(dailyTrend, 'jobs')}
          />
          <StatCard
            label="Margin Earned"
            value={this.formatCurrency(totalMargin)}
            sublabel={`${marginPct}% margin`}
            className="success"
            sparkData={this.buildSparkFromTrend(marginTrend, 'margin')}
          />
          <StatCard
            label="Avg Margin / Job"
            value={this.formatCurrency(avgMargin)}
          />
          <StatCard
            label="Retail Sold"
            value={this.formatCurrency(totalRetail)}
            sublabel="total to customers"
          />
        </div>

        {/* Sales + Your Numbers */}
        <div className="chart-grid">
          <ChartCard title="Sales by Category">
            <DonutChart
              data={marginByCategory.map(c => ({ name: c.category, value: c.count }))}
              showPercent={true}
              colors={['#FF9516', '#5196d3', '#2E56F6', '#6f42c1', '#28a745']}
            />
          </ChartCard>
          <ChartCard title="Your Numbers">
            <div className="insight-item">
              <span className="insight-icon"><i className="fa-sharp fa-solid fa-arrow-trend-up" style={{ color: '#28a745' }} /></span>
              <div>
                <div>{requests} requests this period</div>
                <div className="insight-value">{this.formatCurrency(totalRetail)} retail value</div>
              </div>
            </div>
            <div className="insight-item">
              <span className="insight-icon"><i className="fa-sharp fa-solid fa-coins" style={{ color: '#FF9516' }} /></span>
              <div>
                <div>{this.formatCurrency(totalMargin)} margin earned</div>
                <div className="insight-value">{marginPct}% average margin rate</div>
              </div>
            </div>
            <div className="insight-item">
              <span className="insight-icon"><i className="fa-sharp fa-solid fa-chart-simple" style={{ color: '#5196d3' }} /></span>
              <div>
                <div>{this.formatCurrency(avgMargin)} per job average</div>
                <div className="insight-value">Every sale adds to your bottom line</div>
              </div>
            </div>
          </ChartCard>
        </div>

        {/* Margin + Job Status */}
        <div className="chart-grid">
          <ChartCard title="Margin by Category">
            <DonutChart
              data={marginByCategory.map(c => ({
                name: c.category,
                value: c.margin,
                sublabel: c.retail > 0 ? `${Math.round(c.margin / c.retail * 100)}% margin` : ''
              }))}
              formatValue={(v) => this.formatCurrency(v)}
              colors={['#28a745', '#2E56F6', '#FF9516', '#6f42c1', '#5196d3']}
            />
          </ChartCard>
          <ChartCard title="Where Are My Jobs?">
            <DonutChart
              data={[
                { name: 'Awaiting Confirmation', value: Math.max(0, requests - (data.appointments?.scheduled || 0)), color: '#f59e0b' },
                { name: 'Scheduled', value: data.appointments?.scheduled || 0, color: '#5196d3' },
                { name: 'Ready for Pickup', value: outstandingCount, color: '#28a745' }
              ].filter(item => item.value > 0)}
              showPercent={true}
            />
          </ChartCard>
        </div>

        {/* Margin Trend */}
        {marginTrend.length > 0 && (
          <ChartCard title={`Margin Trend (${this.state.period} days)`} className="trend-chart-card">
            <div style={{ width: '100%', height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={marginTrend}>
                  <defs>
                    <linearGradient id="dealerMarginGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#28a745" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#28a745" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                    tickFormatter={(d) => {
                      const dt = new Date(d);
                      return `${dt.getMonth() + 1}/${dt.getDate()}`;
                    }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                    tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
                    width={50}
                  />
                  <Tooltip
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Margin']}
                    labelFormatter={(d) => new Date(d).toLocaleDateString()}
                  />
                  <Area
                    type="monotone"
                    dataKey="margin"
                    stroke="#28a745"
                    fill="url(#dealerMarginGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        )}

        {/* Specialist Activity */}
        {data.specialists && data.specialists.length > 0 && (
          <ChartCard title="Specialist Activity">
            <div className="tech-grid">
              <div className="tech-chart">
                <DonutChart
                  data={data.specialists.map(s => ({
                    name: s.name,
                    value: s.activeJobs + s.completedJobs
                  }))}
                  showPercent={true}
                  colors={['#5196d3', '#FF9516', '#28a745', '#6f42c1', '#2E56F6', '#f59e0b']}
                />
              </div>
              <div className="tech-table-wrap">
                <table className="tech-table">
                  <thead>
                    <tr>
                      <th>Specialist</th>
                      <th>Active</th>
                      <th>Completed</th>
                      <th>Your Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.specialists.map(s => (
                      <tr key={s.installer_id}>
                        <td style={{ fontWeight: 500 }}>{s.name}</td>
                        <td>
                          {s.activeJobs > 0 ? (
                            <span style={{ color: '#5196d3', fontWeight: 600 }}>{s.activeJobs}</span>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>0</span>
                          )}
                        </td>
                        <td>{s.completedJobs}</td>
                        <td>{this.formatCurrency(s.margin)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </ChartCard>
        )}
      </>
    );
  }

  // --- Helpers ---

  getPipelineStageColor(stage, days) {
    const thresholds = {
      'Lead → Estimate':       { green: 1, yellow: 3 },
      'Estimate → Scheduled':  { green: 3, yellow: 7 },
      'Scheduled → Completed': { green: 3, yellow: 7 },
      'Completed → Paid':      { green: 1, yellow: 3 }
    };
    const t = thresholds[stage] || { green: 2, yellow: 5 };
    if (days <= t.green) return '#28a745';
    if (days <= t.yellow) return '#f59e0b';
    return '#dc3545';
  }

  formatCurrency(val) {
    if (val == null) return '$0';
    if (val >= 1000) return `$${(val / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    return `$${Math.round(val).toLocaleString()}`;
  }

  capitalize(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Convert an object like { "Stripe": { count: 5, amount: 2000 } }
   * to an array like [{ method: "Stripe", count: 5, value: 2000 }]
   */
  objectToArray(obj, keyName, valueMap) {
    if (!obj) return [];
    return Object.entries(obj).map(([key, val]) => {
      const item = { [keyName]: key };
      Object.entries(valueMap).forEach(([srcKey, destKey]) => {
        item[destKey] = val[srcKey] || 0;
      });
      return item;
    });
  }

  /**
   * Build aging buckets from outstanding jobs array
   * Each job has: { request_id, request_total, days_since_finished }
   */
  buildAgingBuckets(jobs) {
    const buckets = [
      { name: 'Today', value: 0, color: '#28a745' },
      { name: '1-3 days', value: 0, color: '#5196d3' },
      { name: '4-7 days', value: 0, color: '#f59e0b' },
      { name: '7+ days', value: 0, color: '#dc3545' }
    ];
    jobs.forEach(j => {
      if (j.days_since_finished == null) return; // skip jobs with no finish date
      const d = j.days_since_finished;
      if (d === 0) buckets[0].value += j.request_total || 0;
      else if (d <= 3) buckets[1].value += j.request_total || 0;
      else if (d <= 7) buckets[2].value += j.request_total || 0;
      else buckets[3].value += j.request_total || 0;
    });
    return buckets.filter(b => b.value > 0);
  }

  buildSparkFromTrend(trend, key) {
    if (!trend || trend.length < 2) return null;
    const step = Math.max(1, Math.floor(trend.length / 10));
    return trend.filter((_, i) => i % step === 0 || i === trend.length - 1).map(t => ({ value: t[key] || 0 }));
  }

  // --- Mock Data (matches server response shape) ---
  getMockData() {
    const days = this.state.period;
    const dailyTrend = [];
    const now = new Date();
    for (let i = days; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const rev = Math.round(400 + Math.random() * 800);
      dailyTrend.push({
        date: d.toISOString().split('T')[0],
        revenue: rev,
        jobs: Math.round(1 + Math.random() * 3)
      });
    }

    return {
      period: { days, dealerId: null },

      aggregate: {
        totalJobs: 142,
        leads: { today: 3, thisWeek: 12, thisMonth: 38, lastMonth: 45 },
        bookedRevenue: 28450,
        leadToPaidRate: 67.61,
        avgDaysLeadToPaid: 8.34
      },

      appointments: {
        scheduled: 96,
        noShowRate: 4.17,
        rescheduleRate: 12.50
      },

      inProgress: {
        avgHoursInProgress: 3.75
      },

      outstanding: {
        count: 5,
        totalRevenue: 3200,
        jobs: [
          { request_id: 1042, request_total: 850, request_type: 'installer', days_since_finished: 0 },
          { request_id: 1038, request_total: 720, request_type: 'dealer', days_since_finished: 2 },
          { request_id: 1035, request_total: 680, request_type: 'installer', days_since_finished: 3 },
          { request_id: 1029, request_total: 550, request_type: 'dealer', days_since_finished: 5 },
          { request_id: 1022, request_total: 400, request_type: 'installer', days_since_finished: 9 }
        ]
      },

      revenueBySource: {
        Dealer: { jobs: 45, revenue: 18200 },
        Retail: { jobs: 97, revenue: 42300 }
      },

      serviceBreakdown: [
        { service_name: 'Full Front PPF', service_category: 'PPF', total_revenue: 14200, total_retail: 16800, service_count: 28 },
        { service_name: 'Full Window Tint', service_category: 'Window Tint', total_revenue: 9800, total_retail: 12400, service_count: 35 },
        { service_name: 'Ceramic Coating', service_category: 'Ceramic', total_revenue: 5400, total_retail: 7200, service_count: 12 },
        { service_name: 'Full Body Wrap', service_category: 'Vinyl Wrap', total_revenue: 3800, total_retail: 5000, service_count: 4 },
        { service_name: 'Partial Front PPF', service_category: 'PPF', total_revenue: 2200, total_retail: 2800, service_count: 8 }
      ],

      payments: {
        byMethod: {
          Stripe: { count: 52, amount: 22400 },
          Check: { count: 8, amount: 4200 },
          Cash: { count: 3, amount: 1200 }
        },
        totalPaid: 27800
      },

      dailyTrend,

      reviews: {
        rating: 4.8,
        totalReviews: 247,
        lastSynced: '2026-02-02T07:00:00.000Z'
      },

      leadAttribution: {
        totalLeads: 85,
        convertedLeads: 34,
        conversionRate: 40,
        bySource: {
          Google: { total: 32, converted: 14, revenue: 8400 },
          Organic: { total: 20, converted: 10, revenue: 5200 },
          Facebook: { total: 15, converted: 5, revenue: 2800 },
          Referral: { total: 10, converted: 3, revenue: 1500 },
          Returning: { total: 8, converted: 2, revenue: 900 }
        }
      },

      pipelineAge: [
        { stage: 'Lead → Estimate', avgDays: 1.8, count: 85 },
        { stage: 'Estimate → Scheduled', avgDays: 4.2, count: 57 },
        { stage: 'Scheduled → Completed', avgDays: 2.1, count: 96 },
        { stage: 'Completed → Paid', avgDays: 3.5, count: 82 }
      ],

      inventory: {
        lowStockAlerts: [
          { product_id: 12, name: 'Ultimate Plus 60"', brand: 'XPEL', current_qty: 1, reorder_point: 5 },
          { product_id: 8, name: 'Color Stable 35%', brand: '3M', current_qty: 0, reorder_point: 3 },
          { product_id: 23, name: 'Ceramic IR 50%', brand: 'XPEL', current_qty: 2, reorder_point: 4 }
        ],
        topMaterialsUsed: [
          { name: 'XPEL Ultimate Plus 60"', category: 'PPF', usage_count: 14 },
          { name: '3M Color Stable 35%', category: 'Window Tint', usage_count: 9 },
          { name: 'XPEL Ceramic IR 50%', category: 'Window Tint', usage_count: 7 },
          { name: 'SunTek CXP 20%', category: 'Window Tint', usage_count: 5 },
          { name: 'XPEL Prime XR+', category: 'PPF', usage_count: 3 }
        ]
      },

      campaigns: [
        { campaign_id: 12, campaign_name: 'Spring PPF Promo', total_leads: 18, converted: 7, revenue: 4200, conversionRate: 38.89 },
        { campaign_id: 15, campaign_name: 'Tint Tuesday', total_leads: 8, converted: 3, revenue: 1200, conversionRate: 37.50 }
      ],

      customers: {
        total: 89,
        repeat: 12,
        repeatRate: 13.48
      },

      technicians: [
        { id: 45, name: 'Mike Thompson', activeServices: 2, completedPeriod: 18, avgServiceHours: 3.4, revenuePeriod: 8200, clockedHoursPeriod: 160.5, jobHoursPeriod: 128.4, utilizationPct: 80 },
        { id: 46, name: 'Steve Garcia', activeServices: 1, completedPeriod: 14, avgServiceHours: 3.8, revenuePeriod: 6400, clockedHoursPeriod: 155.0, jobHoursPeriod: 112.0, utilizationPct: 72 },
        { id: 47, name: 'Carlos Rivera', activeServices: 0, completedPeriod: 11, avgServiceHours: 4.1, revenuePeriod: 4800, clockedHoursPeriod: 148.0, jobHoursPeriod: 88.0, utilizationPct: 59 }
      ]
    };
  }

  render() {
    const { loading, error, data, period, isDesktop } = this.state;
    const { userType } = this.props;
    const isDealer = userType === 'dealer' || userType === 'dealer_group';

    return (
      <div className={`dashboard-container ${!isDesktop ? 'mobile' : ''}`}>
        {/* Header */}
        <div className="page-header">
          <div className="period-selector">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                className={period === d ? 'active' : ''}
                onClick={() => this.handlePeriodChange(d)}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {error && <div className="dashboard-error">{error}</div>}

        {loading && (
          <div className="dashboard-loading">
            <i className="fa-sharp fa-solid fa-spinner fa-spin" /> Loading dashboard...
          </div>
        )}

        {!loading && data && (
          isDealer ? this.renderDealerDashboard() : this.renderInstallerDashboard()
        )}
      </div>
    );
  }
}

export default Dashboard;
